import { IOrgUser, IUserPermission } from "@/types";

import { CheckedUserPermissions, Permission } from "./types";

export const userHasPermission = (user: IOrgUser, permission: Permission) =>
  user.permissions?.some((p) => p.title === permission) ?? false;

export const userPermissionChecks = (
  currentUserPermissions: Permission[],
): CheckedUserPermissions => {
  const {
    RUBI_ORG_OWN_MODIFY_USER_APPS,
    RUBI_ORG_ANY_MODIFY_USER_APPS,
    RUBI_ORG_OWN_MANAGE_ADMINS,
    RUBI_ORG_ANY_MANAGE_ADMINS,
    RUBI_USER_ANY_EDIT_FIELDS,
    RUBI_USER_ORG_EDIT_FIELDS,
    RUBI_ORG_ANY_ADD_USER,
    RUBI_ORG_OWN_ADD_USER,
    ALERT,
    REPORT,
    MESSAGE,
    CONNECT,
    CONNECT_CREATE_MEETING,
    CONNECT_VIEW_RECORDINGS,
    CONNECT_MANAGE_SETTINGS,
    REPORT_V2,
    CAMERA_REGISTRY,
    LOGIN,
    // ALERT PERMISSIONS
    ALERT_CREATE_BULLETIN,
    ALERT_DASHBOARD,
    ALERT_CREATE_GROUP,
    REGISTRY,
    REPORT_V2_CREATE_SURVEY,
    REPORT_V2_VIEW_REPORTS,
    REPORT_V2_VIEW_INDIVIDUAL_REPORT,
    REPORT_V2_VIEW_CITIZEN_ENGAGE,
    REPORT_V2_VIEW_DASHBOARD,
    REPORT_V2_VIEW_SETTINGS,
    REPORT_V2_UPDATE_REPORTS,
    PARTNER,
    PARTNER_VIEW_REPORTS,
    PARTNER_ADD_SUPPLEMENTAL,
    PARTNER_VIEW_AS_ORG,
    PARTNER_WRITE_AS_ORG,
    REGISTRY_VIEW_ENTRIES,
    REGISTRY_UPDATE_ENTRIES,
    REGISTRY_CREATE_ENTRIES,
    REGISTRY_VIEW_DASHBOARD,
    REGISTRY_VIEW_SETTINGS,
    INTEL_VIEW_ENTRIES,
    INTEL_UPDATE_ENTRIES,
    INTEL_DELETE_ENTRIES,
    INTEL_VIEW_DASHBOARD,
    INTEL_VIEW_SETTINGS,
    INTEL,
    MESSAGE_TEMPLATE_WRITE,
    MESSAGE_TEMPLATE_ANY_WRITE,
    MESSAGE_FREEFORM_WRITE,
    MESSAGE_FREEFORM_ANY_WRITE,
    MESSAGE_VIEW_ALL_HISTORY,
    MESSAGE_VIEW_ALL_HISTORY_ANY,
    FEEDBACK,
    FEEDBACK_VIEW_FEEDBACK,
    FEEDBACK_VIEW_DASHBOARD,
    FEEDBACK_VIEW_SETTINGS,
  } = Permission;

  return {
    canManageApps: currentUserPermissions.some((permission: Permission) =>
      [RUBI_ORG_OWN_MODIFY_USER_APPS, RUBI_ORG_ANY_MODIFY_USER_APPS].includes(
        permission,
      ),
    ),
    canManageAdmins: currentUserPermissions.some((permission) =>
      [RUBI_ORG_OWN_MANAGE_ADMINS, RUBI_ORG_ANY_MANAGE_ADMINS].includes(
        permission,
      ),
    ),
    canModifyUsers: currentUserPermissions.some((permission) =>
      [RUBI_USER_ANY_EDIT_FIELDS, RUBI_USER_ORG_EDIT_FIELDS].includes(
        permission,
      ),
    ),
    canCreateUsers: currentUserPermissions.some(
      (permission) =>
        permission === RUBI_ORG_ANY_ADD_USER ||
        permission === RUBI_ORG_OWN_ADD_USER,
    ),
    canAccessAlertsApp: currentUserPermissions.some(
      (permission) => permission === ALERT,
    ),
    canAccessReportsApp: currentUserPermissions.some(
      (permission) => permission === REPORT,
    ),
    canAccessReportsV2App: currentUserPermissions.some(
      (permission) => permission === REPORT_V2,
    ),
    canAccessCameraRegistryApp: currentUserPermissions.some(
      (permission) => permission === CAMERA_REGISTRY,
    ),
    canAccessMessageApp: currentUserPermissions.some(
      (permission) => permission === MESSAGE,
    ),
    canAccessConnectApp: currentUserPermissions.some(
      (permission) => permission === CONNECT,
    ),
    canCreateConnectMeeting: currentUserPermissions.some(
      (permission) => permission === CONNECT_CREATE_MEETING,
    ),
    canViewConnectRecordings: currentUserPermissions.some(
      (permission) => permission === CONNECT_VIEW_RECORDINGS,
    ),
    canManageConnectSettings: currentUserPermissions.some(
      (permission) => permission === CONNECT_MANAGE_SETTINGS,
    ),
    canAccessIntelApp: currentUserPermissions.some(
      (permission) => permission === INTEL,
    ),
    canLogin: currentUserPermissions.some((permission) => permission === LOGIN),
    canCreateBulletin: currentUserPermissions.some(
      (permission) => permission === ALERT_CREATE_BULLETIN,
    ),
    canAccessDashboard: currentUserPermissions.some(
      (permission) => permission === ALERT_DASHBOARD,
    ),
    canCreateGroup: currentUserPermissions.some(
      (permission) => permission === ALERT_CREATE_GROUP,
    ),
    canAccessRegistryApp: currentUserPermissions.some(
      (permission) => permission === REGISTRY,
    ),
    canCreateSurveyRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_CREATE_SURVEY,
    ),
    canViewReportsRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_VIEW_REPORTS,
    ),
    canViewIndividualReportRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_VIEW_INDIVIDUAL_REPORT,
    ),
    canViewCitizenEngageRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_VIEW_CITIZEN_ENGAGE,
    ),
    canViewDashboardRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_VIEW_DASHBOARD,
    ),
    canViewSettingsRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_VIEW_SETTINGS,
    ),
    canUpdateReportsRV2: currentUserPermissions.some(
      (permission) => permission === REPORT_V2_UPDATE_REPORTS,
    ),
    canAccessPartnerApp: currentUserPermissions.some(
      (permission) => permission === PARTNER,
    ),
    canViewPartnerReports: currentUserPermissions.some(
      (permission) => permission === PARTNER_VIEW_REPORTS,
    ),
    canAddPartnerSupplemental: currentUserPermissions.some(
      (permission) => permission === PARTNER_ADD_SUPPLEMENTAL,
    ),
    canViewPartnerAsOrg: currentUserPermissions.some(
      (permission) => permission === PARTNER_VIEW_AS_ORG,
    ),
    canWritePartnerAsOrg: currentUserPermissions.some(
      (permission) => permission === PARTNER_WRITE_AS_ORG,
    ),
    canViewDashboardRegistry: currentUserPermissions.some(
      (permission) => permission === REGISTRY_VIEW_DASHBOARD,
    ),
    canViewEntriesRegistry: currentUserPermissions.some(
      (permission) => permission === REGISTRY_VIEW_ENTRIES,
    ),
    canUpdateEntriesRegistry: currentUserPermissions.some(
      (permission) => permission === REGISTRY_UPDATE_ENTRIES,
    ),
    canCreateEntriesRegistry: currentUserPermissions.some(
      (permission) => permission === REGISTRY_CREATE_ENTRIES,
    ),
    canViewSettingsRegistry: currentUserPermissions.some(
      (permission) => permission === REGISTRY_VIEW_SETTINGS,
    ),
    canViewEntriesIntel: currentUserPermissions.some(
      (permission) => permission === INTEL_VIEW_ENTRIES,
    ),
    canUpdateEntriesIntel: currentUserPermissions.some(
      (permission) => permission === INTEL_UPDATE_ENTRIES,
    ),
    canDeleteEntriesIntel: currentUserPermissions.some(
      (permission) => permission === INTEL_DELETE_ENTRIES,
    ),
    canViewDashboardIntel: currentUserPermissions.some(
      (permission) => permission === INTEL_VIEW_DASHBOARD,
    ),
    canViewSettingsIntel: currentUserPermissions.some(
      (permission) => permission === INTEL_VIEW_SETTINGS,
    ),
    canWriteMessageTemplate: currentUserPermissions.some((permission) =>
      [MESSAGE_TEMPLATE_WRITE, MESSAGE_TEMPLATE_ANY_WRITE].includes(permission),
    ),
    canSendFreeformMessage: currentUserPermissions.some((permission) =>
      [MESSAGE_FREEFORM_WRITE, MESSAGE_FREEFORM_ANY_WRITE].includes(permission),
    ),
    canViewMessageAllHistory: currentUserPermissions.some((permission) =>
      [MESSAGE_VIEW_ALL_HISTORY, MESSAGE_VIEW_ALL_HISTORY_ANY].includes(
        permission,
      ),
    ),
    canAccessFeedbackApp: currentUserPermissions.some(
      (permission) => permission === FEEDBACK,
    ),
    canViewFeedback: currentUserPermissions.some(
      (permission) => permission === FEEDBACK_VIEW_FEEDBACK,
    ),
    canViewDashboardFeedback: currentUserPermissions.some(
      (permission) => permission === FEEDBACK_VIEW_DASHBOARD,
    ),
    canViewSettingsFeedback: currentUserPermissions.some(
      (permission) => permission === FEEDBACK_VIEW_SETTINGS,
    ),
  };
};

export const parseUserPermissions = (permissions: IUserPermission[]) => {
  const userPermissions = permissions?.map((permission) => permission.title);
  if (userPermissions) {
    return userPermissionChecks(userPermissions);
  } else {
    return {} as CheckedUserPermissions;
  }
};
